import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import type { BlockDisplayCondition, BlockDisplayConditionItem } from '@web/types';
import {
    EDisplayConditionOperator,
    EDisplayConditionType,
    EDisplayState,
    EEqualityType,
} from '@common/enums/display-condition.enum';
import { UserFacade } from '@web/store/user/user.facade';
import { ConditionItemSettingsComponent } from '@web/shared/components/web-settings/block-settings/block-display-condition-settings/condition-item-settings/condition-item-settings.component';

const conditionItem = (conditionType: EDisplayConditionType): BlockDisplayConditionItem =>
    ({ conditionType, equality: EEqualityType.EQUAL, value: '' }) as BlockDisplayConditionItem;

@Component({
    selector: 'web-block-display-condition-settings',
    templateUrl: './block-display-condition-settings.component.html',
    styleUrls: ['./block-display-condition-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, TranslateModule, ConditionItemSettingsComponent],
})
export class BlockDisplayConditionSettingsComponent implements OnInit {
    public readonly blockCondition = input.required<BlockDisplayCondition>();
    public readonly valueChangeEvent = output<BlockDisplayCondition>();

    private readonly userFacade = inject(UserFacade);

    public readonly stateOptions = Object.values(EDisplayState);
    public readonly EDisplayConditionOperator = EDisplayConditionOperator;
    public readonly maUserTimezone$ = this.userFacade.memberAreaUserTimezone$;

    private readonly model = signal<BlockDisplayCondition>({
        state: null,
        operator: null,
        conditions: [],
    } as BlockDisplayCondition);

    public readonly state = computed(() => this.model().state);
    public readonly operator = computed(() => this.model().operator);
    public readonly conditions = computed(() => this.model().conditions ?? []);

    /** Read once, like the FormGroup it replaces: the panel owns the value while it is open. */
    public ngOnInit(): void {
        const { state, operator, conditions } = this.blockCondition();

        this.model.set({ state, operator, conditions: [...(conditions ?? [])] } as BlockDisplayCondition);
    }

    public setState(event: Event): void {
        this.patch({ state: (event.target as HTMLSelectElement).value as BlockDisplayCondition['state'] });
    }

    public toggleOperatorValue(operator: EDisplayConditionOperator): void {
        this.patch({ operator } as Partial<BlockDisplayCondition>);
    }

    public setBlockDisplayConditionItem(item: BlockDisplayConditionItem, index: number): void {
        this.setConditions(this.conditions().map((current, i) => (i === index ? { ...current, ...item } : current)));
    }

    public addConditionItem(): void {
        this.setConditions([...this.conditions(), conditionItem(EDisplayConditionType.URL)]);
    }

    public removeConditionItem(index: number): void {
        this.setConditions(this.conditions().filter((_, i) => i !== index));
    }

    public setOnlyAlwaysCondition(): void {
        this.setConditions([conditionItem(EDisplayConditionType.ALWAYS)]);
    }

    private setConditions(conditions: BlockDisplayConditionItem[]): void {
        this.patch({ conditions } as Partial<BlockDisplayCondition>);
    }

    private patch(patch: Partial<BlockDisplayCondition>): void {
        this.model.update((model) => ({ ...model, ...patch }));
        this.valueChangeEvent.emit(this.model());
    }
}
